import { Component, OnDestroy, Inject } from '@angular/core';
import { BreadcrumbService } from 'app/_services/breadcrumb.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, zip } from 'rxjs';
import { HttpService } from 'app/_services/http.service';
import { IUser, IHome, INursingHome, IChatThreadPinned, INewChatMessage, IChatCrypt } from 'app/_interfaces';
import { HomeService } from 'app/_services/home.service';
import { CompanyService } from 'app/_services/company.service';
import { NursingHomeService } from 'app/_services/nursing-home.service';
import { IAppReducerState } from 'app/_reducers';
import * as _ from 'underscore';
import { ChatCryptService } from 'app/_services/chat-crypt.service';
import { ChatPinnedService } from 'app/_services/chat-pinned.service';

//firebase SQL
import * as dc from '@firebasegen/default';
import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnDestroy {
  user: IUser;
  homeData: IHome;
  nursingHomes: INursingHome[];

  residents = [];
  medicalResidents = [];

  currentTime: Number;
  trialNursingHomes: INursingHome[];
  pins = [];
  newChatMessage: INewChatMessage[] = [];

  private destroyed = false;

  private subs = new Subscription();

  constructor(private route: ActivatedRoute,
              private router: Router,
              private bs: BreadcrumbService,
              private hs: HomeService,
              private cs: CompanyService,
              private ccs: ChatCryptService,
              private cps: ChatPinnedService,
              private nhs: NursingHomeService,
              private http: HttpService,
              private store: Store<IAppReducerState>,
              @Inject('CONSTANTS') public CONSTANTS) {
    this.subs.add(
      this.store.select('user').subscribe(user => {
          if (!user.detail) {
            return;
          }

          this.currentTime = Date.now();
          this.user = user.detail;
          this._fetchHomeData();
          this._fetchNursingHomes();
          this.bs.set([]);
        })
    );
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.subs.unsubscribe();
  }

  clickNursingHome(data) {
    const nursingHome = data.selected[0];
    this.router.navigate([`/companies/${nursingHome.nursingHomeGroupId}/${nursingHome.id}`]);
  }

  getNotificationType(key: string): string {
    const status = this.CONSTANTS.NOTIFICATION_TYPE.find(r => r.value === key);
    return status ? status.name : '';
  }

  get nursingHomeCount(): number {
    return new Set(this.homeData.dfreeDevices.map(d => d.nursingHomeId)).size;
  }

  get benNursingHomeCount(): number {
    return new Set(this.homeData.benDfreeDevices.map(d => d.nursingHomeId)).size;
  }

  /**
   * API よりデータを取得
   */
  private _fetchHomeData(): void {
    this.hs.fetchHomeData().subscribe((data: IHome) => {
      this.homeData = data;

      // 施設と医療を分ける
      this.residents = this.homeData.residents.filter(r => !this._isMedicalDevivce(r.serialNumber));
      this.medicalResidents = this.homeData.residents.filter(r => this._isMedicalDevivce(r.serialNumber));

      // 並び替え
      this.residents.sort((a, b) => {
         return new Date(b.latestUrineVolumeCalculationTimestamp).getTime() - new Date(a.latestUrineVolumeCalculationTimestamp).getTime()
      });
      this.medicalResidents.sort((a, b) => {
         return new Date(b.latestUrineVolumeCalculationTimestamp).getTime() - new Date(a.latestUrineVolumeCalculationTimestamp).getTime()
      });
      this.homeData.notifications.sort((a, b) => {
         return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      });
      this.homeData.residents.sort((a, b) => {
         return new Date(b.latestUrineVolumeCalculationTimestamp).getTime() - new Date(a.latestUrineVolumeCalculationTimestamp).getTime()
      });
    });
  }

  /**
   * トライアル中施設一覧
   */
  private _fetchNursingHomes(): void {
    const params = {
      limit: 10000,
    };

    zip(
      this.nhs.fetchNursingHomes(params),
      this.cs.fetchCompanies({limit: 10000})
    ).
    subscribe(([nursingHomes, companies]) => {
      this.nursingHomes = nursingHomes;
      this.trialNursingHomes = _.map(nursingHomes, (nursingHome) => {
        nursingHome.nursingHomeGroupName = _.first(_.where(companies, {id: nursingHome.nursingHomeGroupId}).map(nursingHomeGroup => {
          return nursingHomeGroup.name;
        }));

        if (nursingHome.contractStatus === 'demo' || nursingHome.contractStatus === 'trial') {
          return nursingHome;
        }
      }).filter(Boolean);
      this.trialNursingHomes.reverse();

      // チャット未読件数取得
      this._getChatUnreads();
    });

  }

  /**
   * チャット未読件数
   */
  private async _getChatUnreads() {
    const auth = getAuth();

    onAuthStateChanged(auth, async (user) => {
      if (!user || this.destroyed) {
        return;
      }
      if (!this.nursingHomes) {
        return;
      }
    //   this.cps.get().subscribe(async (res: IChatThreadPinned[]) => {
    //      this.pins = res.map(pin => {
    //       const nursingHome = _.find(this.nursingHomes, { id: pin.nursingHomeId });
    //       return nursingHome ? { pin, nursingHome } : null;
    //     }).filter(Boolean);
    //     for (const row of this.pins) {
    //       const nursingHomeId = row.pin.nursingHomeId;
    //       const chatRead = await dc.getChatRead({
    //         nursingHomeId,
    //         userUuid: user.uid
    //       });

    //       const lastReadAt = chatRead.data.chatReads?.[0]?.lastReadAt;
    //       if (!lastReadAt) {
    //         row.unreadCount = null;
    //         row.allUnread = true;
    //         continue;
    //       }

    //       const unreadCount = await dc.getUnreadChatCount({
    //         nursingHomeId,
    //         userUuid: user.uid,
    //         lastReadAt
    //       });

    //       row.unreadCount = unreadCount.data.chats.length;
    //     }
    //   });

      // 新着メッセージの取得
      const fromDate = new Date(
        Date.now() - 24 * 60 * 60 * 1000
      ).toISOString();

      const result = await dc.getRecentChats({
        createdAt: fromDate
      });

      const newMessage = await Promise.all(result.data.chats.map(async read => {
        const nursingHome = this.nursingHomes.find(d => d.id === read.nursingHomeId);
        const chat = await dc.getChat({
          nursingHomeId: read.nursingHomeId,
          messageUuid: read.messageUuid
        });

        const item = chat.data.chats[0];
        if (!item) {
          return null;
        }
        const decryptedText = await this.decryptMessage(read.nursingHomeId, item);

        const decryptedRow = {
          nursingHomeId: read.nursingHomeId,
          nursingHomeName: nursingHome?.name ?? '',
          nursingHomeGroupId: nursingHome?.nursingHomeGroupId ?? null,
          senderUuid: item.senderUuid,
          senderUserName: item.senderUserName,
          text: await decryptedText,
          realFileName: item.realFileName,
          createdAt: item.createdAt,
        };

        return {
          ...decryptedRow,
          text: this.createPreview(decryptedRow)
        };
      }));

      this.newChatMessage = newMessage.filter(chat => chat.senderUuid !== user.uid);
    });
  }

  private createPreview(message: any): string  {
    // メッセージあり
    if (message.text && message.text?.trim()) {
      return message.text.length > 20
        ? message.text.substring(0, 20) + "..."
        : message.text;
    };

    // ファイルのみ
    if (message.realFileName) {
      return `📎 ${message.realFileName}`;
    }
    return "";
  }

  /**
   * メッセージの復号化
   */
  private async decryptMessage(nursingHomeId, item): Promise<string> {
    if (!item.iv || !item.text) return item.text;
    const chatCrypt: IChatCrypt = {
      messageUuid: item.messageUuid,
      text: item.text,
      iv: item.iv
    }
    const data = await this.ccs.decrypt(nursingHomeId, [chatCrypt]).toPromise();
    if (!data || data.length > 0) {
      return data[0].text;
    }
    return item.text;
  }

  /**
   * 施設 or 医療
   */
  private _isMedicalDevivce(serialNumber: string): boolean {
    // medical 用の判断
    const dum = serialNumber.slice(0, 3);
    if(dum === 'DUM') {
      return true;
    }
    if (serialNumber.charAt(4) === 'M') {
      return true;
    }
    return false;
  }

  /**
   * 外部連携設定
   */
}
